import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useHomeProjectRedirect } from "@/hooks/useHomeProjectRedirect";
import { Shield, ArrowLeft, Key } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { SEO } from "@/components/seo/SEO";
import {
  normalizePendingRegistrationEmail,
  savePendingRegistrationEmail,
} from "@/utils/pendingRegistrationEmail";
import { isApiError } from "@/services/api/client";
import {
  getSignInErrorMessage,
  getTwoFactorErrorMessage,
  isPendingVerificationSignInError,
} from "@/utils/authErrorMessages";
import {
  getPendingRegistrationSelectedPlanPath,
  parsePendingRegistrationSelectedPlan,
  readPendingRegistrationSelectedPlan,
  savePendingRegistrationSelectedPlan,
} from "@/utils/pendingRegistrationSelectedPlan";
import {
  clearRememberedLoginEmail,
  readRememberedLoginEmail,
  saveRememberedLoginEmail,
} from "@/utils/rememberedLoginEmail";

const INVITATION_REDIRECT_KEY = "invitation_redirect";
const INVITATION_REDIRECT_EMAIL_KEY = "invitation_redirect_email";
const SPONSORSHIP_INVITE_TOKEN_PATTERN = /^[a-f0-9]{64}$/i;

const maskEmailForInviteDebug = (email: string): string => {
  const normalizedEmail = normalizePendingRegistrationEmail(email);
  const [localPart = "", domain = ""] = normalizedEmail.split("@");
  if (!localPart || !domain) {
    return normalizedEmail ? "[invalid-email]" : "[empty]";
  }

  return `${localPart.slice(0, 2)}***@${domain}`;
};

const logSponsorshipInviteLoginDebug = (
  event: string,
  metadata: Record<string, unknown>,
): void => {
  console.info(`[sponsorship-invite:login] ${event}`, metadata);
};

const getStoredInvitationRedirectValue = (key: string): string | null =>
  window.sessionStorage.getItem(key);

const getStoredSponsorshipInviteToken = (email: string): string => {
  const normalizedEmail = normalizePendingRegistrationEmail(email);
  if (!normalizedEmail) {
    return "";
  }

  try {
    const expectedEmail = normalizePendingRegistrationEmail(
      getStoredInvitationRedirectValue(INVITATION_REDIRECT_EMAIL_KEY) ?? "",
    );
    if (expectedEmail !== normalizedEmail) {
      return "";
    }

    const storedRedirect = getStoredInvitationRedirectValue(
      INVITATION_REDIRECT_KEY,
    );
    if (!storedRedirect) {
      return "";
    }

    const parsedRedirect = new URL(storedRedirect, window.location.origin);
    if (
      parsedRedirect.origin !== window.location.origin ||
      parsedRedirect.searchParams.get("kind") !== "sponsorship"
    ) {
      return "";
    }

    const pathMatch = parsedRedirect.pathname.match(
      /^\/invitations\/accept\/([^/]+)$/,
    );
    const token = pathMatch ? decodeURIComponent(pathMatch[1]) : "";

    return SPONSORSHIP_INVITE_TOKEN_PATTERN.test(token) ? token : "";
  } catch {
    return "";
  }
};

const getSponsorshipInviteRegisterPath = (
  email: string,
  queryToken: string,
): string | null => {
  const trimmedQueryToken = queryToken.trim();
  const token = SPONSORSHIP_INVITE_TOKEN_PATTERN.test(trimmedQueryToken)
    ? trimmedQueryToken
    : getStoredSponsorshipInviteToken(email);
  if (!token) {
    return null;
  }

  const params = new URLSearchParams({ sponsorshipInviteToken: token });
  const normalizedEmail = normalizePendingRegistrationEmail(email);
  if (normalizedEmail) {
    params.set("email", normalizedEmail);
  }

  return `/register?${params.toString()}`;
};

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPendingGuidance, setShowPendingGuidance] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [searchParams] = useSearchParams();
  const twoFactorInputRef = useRef<HTMLInputElement>(null);
  const { login, verify2FA, cancel2FA, requires2FA, isAuthenticated } = useAuthStore();
  const { isRedirecting, performRedirect } = useHomeProjectRedirect();
  const selectedPlan = useMemo(() => {
    const querySelectedPlan = parsePendingRegistrationSelectedPlan(
      searchParams.get("selectedTier"),
      searchParams.get("billingInterval"),
    );

    return querySelectedPlan ?? readPendingRegistrationSelectedPlan();
  }, [searchParams]);
  const queryEmail = normalizePendingRegistrationEmail(
    searchParams.get("email") ?? "",
  );
  const querySponsorshipInviteToken =
    searchParams.get("sponsorshipInviteToken") ?? "";
  const validQuerySponsorshipInviteToken = SPONSORSHIP_INVITE_TOKEN_PATTERN.test(
    querySponsorshipInviteToken.trim(),
  )
    ? querySponsorshipInviteToken.trim()
    : "";
  const storedSponsorshipInviteToken = getStoredSponsorshipInviteToken(email);
  const checkEmailPath = useMemo(
    () => getPendingRegistrationSelectedPlanPath("/check-email", selectedPlan),
    [selectedPlan],
  );
  const registerPath = useMemo(
    () => getPendingRegistrationSelectedPlanPath("/register", selectedPlan),
    [selectedPlan],
  );
  const inviteRegisterPath =
    getSponsorshipInviteRegisterPath(email, querySponsorshipInviteToken) ??
    registerPath;

  // Redirect to home project or dashboard when authenticated
  useEffect(() => {
    if (isAuthenticated && !isRedirecting) {
      performRedirect();
    }
  }, [isAuthenticated, isRedirecting, performRedirect]);

  // Focus 2FA input when switching to 2FA step
  useEffect(() => {
    if (requires2FA && twoFactorInputRef.current) {
      twoFactorInputRef.current.focus();
    }
  }, [requires2FA]);

  useEffect(() => {
    if (selectedPlan) {
      savePendingRegistrationSelectedPlan(selectedPlan);
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (queryEmail || querySponsorshipInviteToken) {
      logSponsorshipInviteLoginDebug("landed on login link", {
        queryEmail: maskEmailForInviteDebug(queryEmail),
        hasQueryEmail: Boolean(queryEmail),
        hasValidQueryToken: Boolean(validQuerySponsorshipInviteToken),
        hasInvalidQueryToken: Boolean(
          querySponsorshipInviteToken && !validQuerySponsorshipInviteToken,
        ),
      });
    }

    if (queryEmail) {
      setEmail(queryEmail);
      setRememberMe(false);
      return;
    }

    const rememberedEmail = readRememberedLoginEmail();
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, [queryEmail, querySponsorshipInviteToken, validQuerySponsorshipInviteToken]);

  useEffect(() => {
    if (!querySponsorshipInviteToken && !storedSponsorshipInviteToken) {
      return;
    }

    logSponsorshipInviteLoginDebug("register link resolved", {
      email: maskEmailForInviteDebug(email),
      hasQueryToken: Boolean(validQuerySponsorshipInviteToken),
      hasStoredToken: Boolean(storedSponsorshipInviteToken),
      tokenSource: validQuerySponsorshipInviteToken ? "query" : "session",
      registerPathIncludesToken: inviteRegisterPath.includes(
        "sponsorshipInviteToken=",
      ),
    });
  }, [
    email,
    inviteRegisterPath,
    querySponsorshipInviteToken,
    storedSponsorshipInviteToken,
    validQuerySponsorshipInviteToken,
  ]);

  const persistRememberedEmailPreference = () => {
    if (rememberMe) {
      saveRememberedLoginEmail(email);
    } else {
      clearRememberedLoginEmail();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setShowPendingGuidance(false);

    try {
      if (validQuerySponsorshipInviteToken || storedSponsorshipInviteToken) {
        logSponsorshipInviteLoginDebug("login submit with invite context", {
          email: maskEmailForInviteDebug(email),
          hasQueryToken: Boolean(validQuerySponsorshipInviteToken),
          hasStoredToken: Boolean(storedSponsorshipInviteToken),
        });
      }
      await login({ identifier: email, password });
      persistRememberedEmailPreference();
      // If 2FA is required, login won't complete - the requires2FA state will be set
      // If 2FA is not required, navigate will happen via the useEffect above
    } catch (err) {
      if (validQuerySponsorshipInviteToken || storedSponsorshipInviteToken) {
        logSponsorshipInviteLoginDebug("login failed with invite context", {
          email: maskEmailForInviteDebug(email),
          status: isApiError(err) ? err.status : undefined,
          code: isApiError(err) ? err.code : undefined,
          hasMessage: Boolean(getSignInErrorMessage(err)),
        });
      }
      const message = getSignInErrorMessage(err);
      setError(message);
      setShowPendingGuidance(isPendingVerificationSignInError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await verify2FA(twoFactorCode, useBackupCode ? 'backup_code' : '2fa');
      persistRememberedEmailPreference();
      // Navigate happens via the useEffect above when isAuthenticated becomes true
    } catch (err) {
      setError(getTwoFactorErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel2FA = () => {
    cancel2FA();
    setTwoFactorCode("");
    setUseBackupCode(false);
    setError("");
  };

  // 2FA Verification Step
  if (requires2FA) {
    return (
      <>
        <SEO
          title="Sign In to TerraMatrix"
          description="Access your TerraMatrix account to manage your IoT sensor systems, monitor operations, and view analytics dashboards."
          canonical="/login"
          noindex={true}
        />
        <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle>Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>
            {useBackupCode
              ? "Enter one of your backup codes to sign in"
              : "Enter the 6-digit code from your authenticator app"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handle2FASubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="bg-destructive/10 border border-destructive/20 text-destructive px-3 py-2 rounded-md text-sm"
              >
                {error}
              </div>
            )}
            <div className="space-y-2">
              <label htmlFor="login-2fa-code" className="text-sm font-medium">
                {useBackupCode ? "Backup Code" : "Authentication Code"}
              </label>
              <Input
                id="login-2fa-code"
                ref={twoFactorInputRef}
                type="text"
                inputMode={useBackupCode ? "text" : "numeric"}
                placeholder={useBackupCode ? "Enter backup code" : "000000"}
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\s/g, ''))}
                required
                disabled={isLoading || isRedirecting}
                maxLength={useBackupCode ? 16 : 6}
                className={useBackupCode ? "" : "text-center text-2xl tracking-widest font-mono"}
                autoComplete="one-time-code"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || isRedirecting || !twoFactorCode || (!useBackupCode && twoFactorCode.length !== 6)}
            >
              {isRedirecting ? "Redirecting to your home project..." : isLoading ? "Verifying..." : "Verify"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setTwoFactorCode("");
              setError("");
            }}
            className="w-full"
          >
            <Key className="h-4 w-4 mr-2" />
            {useBackupCode ? "Use authenticator app instead" : "Use a backup code"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel2FA}
            className="w-full text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to login
          </Button>
        </CardFooter>
      </Card>
      </>
    );
  }

  // Regular Login Form
  return (
    <>
      <SEO
        title="Sign In to TerraMatrix"
        description="Access your TerraMatrix account to manage your IoT sensor systems, monitor operations, and view analytics dashboards."
        canonical="/login"
        noindex={true}
      />
      <Card>
        <CardHeader>
          <CardTitle>Sign In</CardTitle>
          <CardDescription>
            Enter your credentials to access your workspace
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="bg-destructive/10 border border-destructive/20 text-destructive px-3 py-2 rounded-md text-sm"
              >
                {error}
              </div>
            )}
            {showPendingGuidance && (
              <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 text-sm">
                <p className="text-muted-foreground">
                  This account may still need email verification. Request a new
                  verification link, then return here to sign in.
                </p>
                <Button asChild variant="secondary" className="mt-3 w-full">
                  <Link
                    to={checkEmailPath}
                    onClick={() => savePendingRegistrationEmail(email)}
                  >
                    Check email or resend link
                  </Link>
                </Button>
              </div>
            )}
            <div className="space-y-2">
              <label htmlFor="login-email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="login-email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setShowPendingGuidance(false);
                }}
                required
                disabled={isLoading || isRedirecting}
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="login-password" className="text-sm font-medium">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="login-password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading || isRedirecting}
                autoComplete="current-password"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading || isRedirecting}
                  className="w-4 h-4 rounded cursor-pointer border-input accent-primary focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:outline-none"
                />
                <span className="text-sm text-muted-foreground">
                  Remember email
                </span>
              </label>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || isRedirecting || !email || !password}
            >
              {isRedirecting
                ? "Redirecting to your home project..."
                : isLoading
                  ? "Signing in..."
                  : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
      <p className="text-center text-sm text-muted-foreground mt-4">
        Don't have an account?{" "}
        <Link
          to={inviteRegisterPath}
          className="text-primary hover:underline font-medium"
        >
          Create one
        </Link>
      </p>
    </>
  );
};
